import React from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import db from '../../lib/db';
import { currentChannelShareKey } from '../../lib/meta';
import { cn } from '../../lib/utils';

import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import ProfilePopoverContent from '../ProfilePopoverContent';
import EntityPopover from '../shared/EntityPopover';
import Flexicon from '../shared/Flexicon';
import LocalTime from './LocalTime';
import RichdownContent from './RichdownContent';
import { WidgetPublishedAnnouncement } from './WidgetAnnouncement';

const activateChannel = (channel) => {
  window.dispatchEvent(new CustomEvent('output:activate-channel', { detail: { channelId: channel.id } }));
};

// the main sentence component of the announcement
const InlinePhrase = ({ children }) => <span className="min-w-0 break-words whitespace-normal">{children}</span>;

const AnnouncementText = ({ announcement }) => (
  <div className="min-w-0 break-words whitespace-normal [&_.richdown-content--base]:inline [&_p]:inline">
    <RichdownContent html={announcement?.text} entitySnapshots={announcement?.entity_snapshots} />
  </div>
);

// Clicking a name or avatar shows the profile popover (via EntityPopover's clickEnabled) — never a
// DM or account settings — so swallow the click before it reaches an interactive ancestor.
const swallowClick = (event) => event?.stopPropagation();

const ProfileFlexicon = ({ profile, isReadOnly = false, size = 'size-4' }) => {
  if (!profile) return null;

  // render non-interactive version in shared channel mode
  if (isReadOnly) {
    return (
      <span className={cn('inline-flex', size)}>
        <Flexicon resource={profile} size={size} />
      </span>
    );
  }

  const popoverContent = profile?.id ? <ProfilePopoverContent profile={profile} /> : null;

  return (
    <EntityPopover content={popoverContent} interactive={true} clickEnabled={true}>
      <span className={cn('inline-flex cursor-pointer', size)} onClick={swallowClick}>
        <Flexicon resource={profile} size={size} />
      </span>
    </EntityPopover>
  );
};

const ProfileLink = ({ profile, isReadOnly = false, className = '' }) => {
  const fallback = 'Someone';

  if (!profile) return fallback;

  // render non-interactive version in shared channel mode
  if (isReadOnly) {
    return <span className={cn('text-pretty break-words', className)}>{profile.full_name || fallback}</span>;
  }

  const popoverContent = profile?.id ? <ProfilePopoverContent profile={profile} /> : null;

  return (
    <EntityPopover content={popoverContent} interactive={true} clickEnabled={true}>
      <span tabIndex={0} className={cn('cursor-pointer text-pretty break-words', className)} onClick={swallowClick}>
        {profile.full_name || fallback}
      </span>
    </EntityPopover>
  );
};

const ChannelLink = ({ channel, breakout = false, isReadOnly = false }) => {
  const fallback = breakout ? 'a breakout' : 'a channel';
  if (!channel) return fallback;

  // render non-interactive version in shared channel mode
  if (isReadOnly) {
    return <span className="text-pretty break-words">{channel.name || fallback}</span>;
  }

  return (
    <span tabIndex={0} className="cursor-pointer text-pretty break-words underline" onClick={() => activateChannel(channel)}>
      {channel.name || fallback}
    </span>
  );
};

// Seed from the row's hydrated entity snapshots while the Dexie query resolves —
// useLiveQuery returns undefined on its first render, which would flash the
// "Someone"/"a breakout" fallbacks for a frame even with a warm entity cache.
const useAnnouncementProfile = (announcement, profileId) => {
  const { workspaceId } = useRouteWorkspace();
  const live = useLiveQuery(() => {
    if (!profileId || !workspaceId) return null;
    return db.profiles.get([workspaceId, profileId]);
  }, [profileId, workspaceId]);
  return live ?? (profileId ? announcement?.entity_snapshots?.profile?.[profileId] : null) ?? null;
};

const useAnnouncementChannel = (announcement, channelId) => {
  const { workspaceId } = useRouteWorkspace();
  const live = useLiveQuery(() => {
    if (!channelId || !workspaceId) return null;
    return db.channels.get([workspaceId, channelId]);
  }, [channelId, workspaceId]);
  return live ?? (channelId ? announcement?.entity_snapshots?.channel?.[channelId] : null) ?? null;
};

// Membership changes (joins and leaves) render as a centered avatar + sentence row,
// unlike the left-aligned sentence treatment the other modes keep.
//
// -ml-[22px] + the 22px-wider box cancel the row wrapper's pl-[22px] content inset
// (see DayDivider), so this flex box spans the column symmetrically and centers on
// its true axis. shrink-0 stops the parent flex row from eating the extra width and
// re-skewing the box.
const MembershipChange = ({ children, isReadOnly = false, profile }) => (
  <div className="-ml-[22px] flex w-[calc(100%+22px)] shrink-0 items-center justify-center gap-2">
    <ProfileFlexicon profile={profile} isReadOnly={isReadOnly} size="size-6" />
    <InlinePhrase>
      <span className="text-[13px]">{children}</span>
    </InlinePhrase>
  </div>
);

const ChannelJoin = ({ announcement, isReadOnly = false }) => {
  const profile = useAnnouncementProfile(announcement, announcement?.data?.profile_id);
  const inviter = useAnnouncementProfile(announcement, announcement?.data?.inviter_profile_id);

  return (
    <MembershipChange profile={profile} isReadOnly={isReadOnly}>
      <ProfileLink profile={profile} isReadOnly={isReadOnly} />
      {inviter ? (
        <>
          {' was added by '}
          <ProfileLink profile={inviter} isReadOnly={isReadOnly} />
          {' at '}
        </>
      ) : (
        ' joined at '
      )}
      <LocalTime timestamp={announcement.timestamp} format="%-I:%M %p" />
    </MembershipChange>
  );
};

const ChannelLeave = ({ announcement, isReadOnly = false }) => {
  const profile = useAnnouncementProfile(announcement, announcement?.data?.profile_id);
  const remover = useAnnouncementProfile(announcement, announcement?.data?.remover_profile_id);

  return (
    <MembershipChange profile={profile} isReadOnly={isReadOnly}>
      <ProfileLink profile={profile} isReadOnly={isReadOnly} />
      {remover ? (
        <>
          {' was removed by '}
          <ProfileLink profile={remover} isReadOnly={isReadOnly} />
          {' at '}
        </>
      ) : (
        ' left at '
      )}
      <LocalTime timestamp={announcement.timestamp} format="%-I:%M %p" />
    </MembershipChange>
  );
};

const BreakoutActivity = ({ announcement, action, isReadOnly = false }) => {
  const profile = useAnnouncementProfile(announcement, announcement?.data?.profile_id);
  const channel = useAnnouncementChannel(announcement, announcement?.data?.breakout_channel_id);

  return (
    <>
      <ProfileFlexicon profile={profile} isReadOnly={isReadOnly} />
      <InlinePhrase>
        <ProfileLink profile={profile} isReadOnly={isReadOnly} />
        {` ${action} `}
        <ChannelLink channel={channel} breakout={true} isReadOnly={isReadOnly} />
        {' at '}
        <LocalTime timestamp={announcement.timestamp} format="%-I:%M %p" />
      </InlinePhrase>
    </>
  );
};

const ChannelMetadata = ({ announcement, isReadOnly = false }) => {
  const channelNameUpdate = announcement?.data?.channel_name_update;
  const channelFlexiconUpdate = announcement?.data?.channel_flexicon_update;
  const profile = useAnnouncementProfile(announcement, announcement?.data?.profile_id);

  let action = 'updated some information';
  if (channelNameUpdate && channelFlexiconUpdate) {
    action = `renamed to “${channelNameUpdate}” and changed the icon`;
  } else if (channelNameUpdate) {
    action = `renamed to “${channelNameUpdate}”`;
  } else if (channelFlexiconUpdate) {
    action = 'changed the icon';
  }

  return (
    <>
      <ProfileFlexicon profile={profile} isReadOnly={isReadOnly} />
      <InlinePhrase>
        <ProfileLink profile={profile} isReadOnly={isReadOnly} /> {action} at{' '}
        <LocalTime timestamp={announcement.timestamp} format="%-I:%M %p" />
      </InlinePhrase>
    </>
  );
};

const WidgetPublished = ({ announcement, isReadOnly = false }) => {
  const profile = useAnnouncementProfile(announcement, announcement?.data?.profile_id);
  const widgetTitle = announcement?.data?.widget_title || 'A widget';

  return (
    <>
      <ProfileFlexicon profile={profile} isReadOnly={isReadOnly} />
      <InlinePhrase>
        {`“${widgetTitle}” was added to the chat by `}
        <ProfileLink profile={profile} isReadOnly={isReadOnly} />
      </InlinePhrase>
    </>
  );
};

const Contents = ({ announcement, isReadOnly = false }) => {
  switch (announcement.data?.mode) {
    case 'channel_join':
      return <ChannelJoin announcement={announcement} isReadOnly={isReadOnly} />;

    case 'channel_leave':
      return <ChannelLeave announcement={announcement} isReadOnly={isReadOnly} />;

    case 'breakout_open':
      return <BreakoutActivity announcement={announcement} action="opened" isReadOnly={isReadOnly} />;

    case 'breakout_close':
      return <BreakoutActivity announcement={announcement} action="closed" isReadOnly={isReadOnly} />;

    case 'channel_metadata':
      return <ChannelMetadata announcement={announcement} isReadOnly={isReadOnly} />;

    case 'widget_published':
      return <WidgetPublished announcement={announcement} isReadOnly={isReadOnly} />;

    default:
      if (isReadOnly && announcement?.text) return <AnnouncementText announcement={announcement} />;

      return (
        <>
          <InlinePhrase>Unknown announcement type</InlinePhrase>
          <div className="rounded border border-gray-200 bg-white p-1 font-mono text-3xs whitespace-pre-wrap text-gray-600">
            {JSON.stringify(announcement.data, null, 2)}
          </div>
        </>
      );
  }
};

const Announcement = ({ announcement }) => {
  const isReadOnly = !!currentChannelShareKey();

  // Widget arrivals leave the sentence row for the right-side chip the rest of
  // the widget events use. Shared channels keep the sentence: there is no
  // widgets pane over there for the chip to jump to.
  if (!isReadOnly && announcement?.data?.mode === 'widget_published' && announcement.data.widget_id) {
    return <WidgetPublishedAnnouncement announcement={announcement} />;
  }

  return (
    <div data-debug-id={announcement.id} data-debug-sort-order={announcement.sort_order} className="py-4">
      <div className="channel-announcement flex items-start justify-start gap-2 px-2 text-[11px] font-blazeit text-gray-500">
        <Contents announcement={announcement} isReadOnly={isReadOnly} />
      </div>
    </div>
  );
};

export default Announcement;
